import { create } from "zustand";
import { useAuth } from "@/features/auth/hooks/useAuth";

/** A held organization switch: call `proceed` to finish it or `cancel` to drop it. */
export interface TenantSwitchRequest {
  proceed: () => void;
  cancel: () => void;
}

/**
 * Asked before anything changes the active tenant. Return `true` to hold the switch (for example
 * to ask about unsaved changes) and settle the request later; return `false` to let it happen now.
 */
export type TenantSwitchGuard = (request: TenantSwitchRequest) => boolean;

interface TenantSwitchGuardState {
  guard: TenantSwitchGuard | null;
  setGuard: (guard: TenantSwitchGuard | null) => void;
}

/** One guard at a time: the page that currently holds unsaved work. */
export const useTenantSwitchGuardStore = create<TenantSwitchGuardState>((set) => ({
  guard: null,
  setGuard: (guard) => set({ guard }),
}));

/** Registers `guard` and returns the cleanup that removes it, unless another guard replaced it. */
export function registerTenantSwitchGuard(guard: TenantSwitchGuard): () => void {
  useTenantSwitchGuardStore.getState().setGuard(guard);
  return () => {
    if (useTenantSwitchGuardStore.getState().guard === guard) useTenantSwitchGuardStore.getState().setGuard(null);
  };
}

/**
 * Runs `switchTenant` once the registered guard allows it: at once when nothing holds it, later when
 * the user confirms. Resolves `true` after it ran and `false` when the user kept editing. A request is
 * settled once, so a double click on the dialog cannot switch or navigate twice.
 */
export function runGuardedTenantSwitch(switchTenant: () => void): Promise<boolean> {
  return new Promise((resolve) => {
    let isSettled = false;
    const request: TenantSwitchRequest = {
      proceed: () => {
        if (isSettled) return;
        isSettled = true;
        switchTenant();
        resolve(true);
      },
      cancel: () => {
        if (isSettled) return;
        isSettled = true;
        resolve(false);
      },
    };
    if (useTenantSwitchGuardStore.getState().guard?.(request)) return;
    request.proceed();
  });
}

/** Thrown when a flow needed another organization and the user chose to keep editing instead. */
export class TenantSwitchCancelledError extends Error {
  constructor() {
    super("The organization switch was cancelled to keep unsaved changes");
    this.name = "TenantSwitchCancelledError";
  }
}

/** For a flow that must run under `tenantId`: switches through the guard, or throws when the user keeps editing. */
export async function ensureActiveTenant(tenantId: string): Promise<void> {
  if (useAuth.getState().activeTenantId === tenantId) return;
  const isSwitched = await runGuardedTenantSwitch(() => useAuth.getState().setActiveTenant(tenantId));
  if (!isSwitched) throw new TenantSwitchCancelledError();
}
